import LRUCache from 'lru-cache';

/**
 * Memoizes a given function with an LRU (least recently used) cache.
 * @param func: the function to be memoized
 * @param lruCacheOptions: options for the lru-cache library
 * @param getKeyFromArgs: function to generate a cache key from the arguments to the func
 * @returns: the memoized function
 */
export default function lruMemoize<A extends Array<unknown>, V, K>(
  func: (...a: A) => V,
  lruCacheParams: LRUCache.Options<K, V>,
  getKeyFromArgs: (...a: A) => K
): ((...a: A) => V) & {_cache: LRUCache<K, V>} {
  const cache = new LRUCache(lruCacheParams);
  const fn = (...args: A): V => {
    const cacheKey: K = getKeyFromArgs(...args);
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey) as V;
    } else {
      const result = func(...args);
      cache.set(cacheKey, result);
      return result;
    }
  };
  // Cache is exposed so it can be reset in tests
  fn._cache = cache;
  return fn;
}
